import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {useGetConfirmView} from '../../../hooks/productions/useProductions';
import {GetConfirmViewRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const ConfirmViewApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(216);
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetConfirmViewRequestParam = {
    productionId: productionId,
    securityGroupId: securityGroupId,
    ModuleType: moduleType,
  };

  const {
    data: confirmViewData,
    isLoading,
    error,
    refetch,
  } = useGetConfirmView(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (confirmViewData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(confirmViewData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'Error' || status === 'FAILED') return 'error';
    if (status === 'Success' || status === 'COMPLETE') return 'success';
    if (status === 'Warning') return 'warning';
    return 'default';
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Confirm View API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get production confirm view with QC results, production status, and
          commit information.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Confirm View
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/confirm-view
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get production confirm view with QC result data list, production
            status, and commit/uncommit information.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduleType}
              onChange={(e) => setModuleType(e.target.value)}
              size="small"
              placeholder="e.g., PRODUCTION, EXPORT"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="confirm-view-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="confirm-view-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="confirm-view-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {confirmViewData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="confirm-view-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Fetching confirm view...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while fetching confirm view'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {confirmViewData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Confirm View Data
              </Typography>

              {/* Summary Information */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Summary Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Current Status:</strong>{' '}
                        <Chip
                          label={confirmViewData.productionCurrentStatus}
                          color={getStatusColor(
                            confirmViewData.productionCurrentStatus
                          )}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Status Code:</strong>{' '}
                        <Chip
                          label={confirmViewData.productionStatusCode}
                          color={getStatusColor(
                            confirmViewData.productionStatusCode
                          )}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is First Commit:</strong>{' '}
                        {confirmViewData.isFirstCommit ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Disable Commit Button:</strong>{' '}
                        {confirmViewData.disableCommitButton ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is Commit Or Uncommit:</strong>{' '}
                        {confirmViewData.isCommitOrUncommit ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Production Result Details */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Production Result Details
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Name:</strong>{' '}
                        {confirmViewData.productionResultDetails.productionName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production ID:</strong>{' '}
                        {confirmViewData.productionResultDetails.productionId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Status:</strong>{' '}
                        <Chip
                          label={
                            confirmViewData.productionResultDetails
                              .productionStatus
                          }
                          color={getStatusColor(
                            confirmViewData.productionResultDetails
                              .productionStatus
                          )}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production State Code:</strong>{' '}
                        <Chip
                          label={
                            confirmViewData.productionResultDetails
                              .productionStateCode
                          }
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Documents Generated:</strong>{' '}
                        {
                          confirmViewData.productionResultDetails
                            .numberOfDocumentsGenerated
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Family Members Generated:</strong>{' '}
                        {
                          confirmViewData.productionResultDetails
                            .numberOfFamilyMembersGenerated
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Natively Generated Docs:</strong>{' '}
                        {
                          confirmViewData.productionResultDetails
                            .nativelyGeneratedDocs
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Destination Path:</strong>{' '}
                        {
                          confirmViewData.productionResultDetails
                            .productionDestinationPath
                        }
                      </Typography>
                    </Grid>
                    {confirmViewData.productionResultDetails
                      .productionDATFilePath && (
                      <Grid item xs={12}>
                        <Typography className="typo-text body3_size">
                          <strong>Production DAT File Path:</strong>{' '}
                          {
                            confirmViewData.productionResultDetails
                              .productionDATFilePath
                          }
                        </Typography>
                      </Grid>
                    )}
                    {confirmViewData.productionResultDetails
                      .productionArchivePath && (
                      <Grid item xs={12}>
                        <Typography className="typo-text body3_size">
                          <strong>Production Archive Path:</strong>{' '}
                          {
                            confirmViewData.productionResultDetails
                              .productionArchivePath
                          }
                        </Typography>
                      </Grid>
                    )}
                    {confirmViewData.productionResultDetails
                      .shareableLinksExpireOn && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="typo-text body3_size">
                          <strong>Shareable Links Expire On:</strong>{' '}
                          {
                            confirmViewData.productionResultDetails
                              .shareableLinksExpireOn
                          }
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Box>

              {/* QC Result Data List */}
              {confirmViewData.productionResultDetails.qcResultDataList &&
                confirmViewData.productionResultDetails.qcResultDataList
                  .length > 0 && (
                  <Box sx={{marginBottom: 3}}>
                    <Typography
                      className="typo-text subhead3_size"
                      sx={{marginBottom: 1}}
                    >
                      QC Result Data List
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>QC Type</strong>
                            </TableCell>
                            <TableCell>
                              <strong>QC Result</strong>
                            </TableCell>
                            <TableCell>
                              <strong>QC Label</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Production Level QC</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Help Text</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {confirmViewData.productionResultDetails.qcResultDataList.map(
                            (qcResult, index) => (
                              <TableRow key={index}>
                                <TableCell>{qcResult.qcType}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={qcResult.qcResult}
                                    color={getStatusColor(qcResult.qcResult)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{qcResult.qcLabel}</TableCell>
                                <TableCell>
                                  {qcResult.isProductionLevelQC ? 'Yes' : 'No'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    maxWidth: 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={qcResult.qcHelpText}
                                >
                                  {qcResult.qcHelpText}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(confirmViewData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ConfirmViewApiTest;
